import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';

import CustomPicker from '../components/color/ColorPicker';
import { parseToRgb, rgb as polishedRgb } from 'polished';
import rgb2ryb from 'rgb2ryb';
import ryb2rgb from 'ryb2rgb';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';
import BackToTop from '../components/BackToTop';

const convert = (hex: string) => {
  const rgb = parseToRgb(hex);
  const ryb = rgb2ryb.rgb2ryb([rgb.red, rgb.green, rgb.blue]);
  const complimentary = rgb2ryb.complimentary(ryb);
  const backToRgb = ryb2rgb(complimentary).map(x => Math.round(x));
  return polishedRgb(backToRgb[0], backToRgb[1], backToRgb[2]);
};

const PageTemplate = css`
.site-main {
background #fff;
padding-bottom: 4vw;
}

.post-content {
max-width: 1000px;
margin: 0 auto;
}
.post-full-content {
  background: transparent;
}
p {
  font-size: 2rem !important;

}
h2 {
  font-size: 3rem;
}
h2::before {
all: unset !important;
}
h3 {
text-decoration: none !important;
padding-top: 5rem;
text-align: center;
}

.slider-picker > div:first-of-type {
  height: 75px !important;
}
.hue-horizontal > div > div:first-of-type {
  height: 84px !important;
  transform: translate(-7px, -4px) !important;
  opacity: 0.9;
  cursor: pointer;
}

.result-data {
  margin-top: 4rem;
}

.result-data hr {
  margin: 2rem 0;
}

#seg-1, #seg-2,#seg-3, #seg-4,#seg-5, #seg-6,#seg-7, #seg-8,#seg-9, #seg-10,#seg-11, #seg-12 {
  cursor: pointer;
}
#line {
  transition: transform .25s ease;
}

svg {
  max-width: 100%;
  height: auto;
}
`;

const FactsBox = styled.div`
  margin-top: 15rem;
  background: #EFFAF2;
  border-radius: 8px;
  padding: 1.3rem 1.3rem 0.45rem;

  @media (max-width: 750px) {
    padding: 0.1rem 0.1rem 0.1rem;
  }

  li:first-letter {
    color: #ACE3BA;
  }

  h3 {
    padding-top: 0;
  }
`;

const Logo = styled.div`
  width: 75px;
  height: 75px;
  border-radius: 50%;
  margin: 0 auto 2rem;
  background: linear-gradient(90deg, #2bd3f7 50%, #ff490b 50%);
  box-shadow: rgba(39, 44, 49, 0.16) 0px 0px 28px, rgba(39, 44, 49, 0.13) 1px 3px 8px;

  :hover {
    animation: logo-anim 2.5s linear infinite;
  }

  @keyframes logo-anim {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const PickedColor = styled.div`
  background: ${props => `${props.color}`};
  width: 200px;
  height: 200px;
  border-radius: 50%;
  margin: 0 auto;

  @media (max-width: 650px) {
    width: 125px;
    height: 125px;
  }
`;

const ComplementaryColor = styled.div`
  background: ${props => `${convert(props.color || '#FF0000')}`};
  width: 200px;
  height: 200px;
  border-radius: 50%;
  margin: 0 auto;

  @media (max-width: 650px) {
    width: 125px;
    height: 125px;
  }
`;

const Result = styled.div`
  display: grid;
  grid-gap: 2rem;
  grid-template-columns: 1fr 1fr;
`;

interface ComplementaryColorsState {
  hex: string;
}

class ComplementaryColors extends React.Component<undefined, ComplementaryColorsState> {
  state = {
    hex: '#40bf77',
  };

  handleChangeComplete = data => {
    if (data.hex !== this.state.hex) {
      this.setState({ hex: data.hex });
    }
  };

  rotateSegment = (angle: number) => {
    const lineEl = document.getElementById('line');

    if (lineEl) {
      lineEl.style.transformOrigin = '50% 50%';
      lineEl.style.transform = `rotate(${angle}deg)`;
    }
  };

  render() {
    return (
      <IndexLayout>
        <Helmet>
          <title>Complementary Color Picker | Giggster Guide</title>
          <meta
            name="description"
            content="A free interactive tool to get the complementary color code for any color. This is useful to photographers for color grading and color gel selection."
          />

          <meta property="og:site_name" content="Giggster Guide" />
          <meta property="og:type" content="article" />
          <meta property="og:title" content="Complementary Color Picker" />
          <meta
            property="og:description"
            content="A free interactive tool to get the complementary color code for any color. This is useful to photographers for color grading and color gel selection."
          />
          <meta property="og:url" content="https://giggster.com/guide/complementary-colors/" />
          <meta
            property="og:image"
            content="https://giggster.com/guide/covers/complementary-colors.png"
          />
          <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Complementary Color Picker" />
          <meta
            name="twitter:description"
            content="A free interactive tool to get the complementary color code for any color. This is useful to photographers for color grading and color gel selection."
          />
          <meta name="twitter:url" content="https://giggster.com/guide/complementary-colors/" />
          <meta
            name="twitter:image"
            content="https://giggster.com/guide/covers/complementary-colors.png"
          />
          <meta name="twitter:site" content="@Giggster" />
          <meta name="twitter:creator" content="@Giggster" />
        </Helmet>
        <Wrapper css={PageTemplate}>
          <header css={[outer, SiteHeader]}>
            <div css={inner}>
              <SiteNav />
            </div>
          </header>
          <main id="site-main" className="site-main" css={[SiteMain, outer]}>
            <article className="post page" css={[PostFull, NoImage]}>
              <PostFullHeader>
                <PostFullTitle>
                  <Logo />
                  Complementary Color Picker
                </PostFullTitle>
                <p>
                  Below you can pick on paste-in a color and you'll get it's complementary color.
                </p>
                {/*<ShareButtons
                  title={'Complementary Color Picker'}
                  slug={'/complementary-colors/'}
                  imgSrc={'covers/complementary-colors.png'}
                />*/}
              </PostFullHeader>

              <PostFullContent className="post-full-content">
                <div className="post-content text-center">
                  <h2>Start by picking a Hue</h2>
                  <CustomPicker color={this.state} onChangeComplete={this.handleChangeComplete} />

                  <Result>
                    <div>
                      <h3 style={{ color: this.state.hex }}>Your picked color</h3>
                      <PickedColor color={this.state.hex} />
                      <div className="result-data">
                        HEX:
                        <br />
                        <strong style={{ textTransform: 'uppercase' }}>{this.state.hex}</strong>
                        <hr />
                        Red: <strong>{parseToRgb(this.state.hex).red}</strong>
                        <br />
                        Green: <strong>{parseToRgb(this.state.hex).green}</strong>
                        <br />
                        Blue: <strong>{parseToRgb(this.state.hex).blue}</strong>
                      </div>
                    </div>
                    <div>
                      <h3 style={{ color: convert(this.state.hex) }}>Complementary color</h3>
                      <ComplementaryColor color={this.state.hex} />
                      <div className="result-data">
                        HEX:
                        <br />
                        <strong style={{ textTransform: 'uppercase' }}>
                          {convert(this.state.hex)}
                        </strong>
                        <hr />
                        Red: <strong>{parseToRgb(convert(this.state.hex)).red}</strong>
                        <br />
                        Green: <strong>{parseToRgb(convert(this.state.hex)).green}</strong>
                        <br />
                        Blue: <strong>{parseToRgb(convert(this.state.hex)).blue}</strong>
                      </div>
                    </div>
                  </Result>

                  <hr />
                  <h2>The Artist (RYB) color wheel</h2>
                  <p>
                    Below you can see a traditional color wheel that should help picture how
                    complementary colors work.
                  </p>
                  <svg width="700px" height="700px" viewBox="0 0 2000 2000" version="1.1">
                    <g
                      id="artist-ryb-color-wheel"
                      stroke="none"
                      strokeWidth="1"
                      fill="none"
                      fillRule="evenodd"
                    >
                      <g
                        id="all-segments"
                        transform="translate(1000.000000, 1000.000000) rotate(-15.000000) translate(-1000.000000, -1000.000000) translate(-1.000000, 0.000000)"
                      >
                        <path
                          d="M1001,0 C1358.27,0 1688.4,190.6 1867.03,500 C2045.66,809.4 2045.66,1190.6 1867.03,1500 C1688.4,1809.4 1358.27,2000 1001,2000 C643.73,2000 313.6,1809.4 134.97,1500 C-43.66,1190.6 -43.66,809.4 134.97,500 C313.6,190.6 643.73,0 1001,0 L1001,500 C822.37,500 657.31,595.3 567.99,750 C478.67,904.7 478.67,1095.3 567.99,1250 C657.31,1404.7 822.37,1500 1001,1500 C1179.63,1500 1344.69,1404.7 1434.01,1250 C1523.33,1095.3 1523.33,904.7 1434.01,750 C1344.69,595.3 1179.63,500 1001,500 L1001,0 Z"
                          id="base"
                          fillOpacity="0.002"
                          fill="#455A64"
                        />
                        <path
                          d="M1001.125,0.125007806 L1001.125,499.875016 C1088.82315,499.896934 1174.97994,522.985169 1250.95424,566.819265 L1500.82924,134.01576 C1348.89502,46.3205995 1176.56321,0.146930107 1001.125,0.12500781 Z"
                          id="seg-1"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#FF0000"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(0)}
                        />
                        <path
                          d="M1501.04575,134.140761 L1251.17075,566.944255 C1327.11535,610.811812 1390.18819,673.88465 1434.05574,749.829253 L1866.85924,499.95425 C1779.12362,348.037656 1652.96234,221.876376 1501.04575,134.140761 Z"
                          id="seg-2"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#C71585"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(30)}
                        />
                        <path
                          d="M1866.98424,500.170756 L1434.18074,750.045764 C1478.01483,826.020063 1501.10307,912.176848 1501.12498,999.875 L2000.87499,999.875 C2000.85307,824.436793 1954.6794,652.104983 1866.98424,500.170756 Z"
                          id="seg-3"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#8A2BE2"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(60)}
                        />
                        <path
                          d="M2000.87499,1000.125 L1501.12498,1000.125 C1501.10307,1087.82315 1478.01483,1173.97994 1434.18074,1249.95424 L1866.98424,1499.82924 C1954.6794,1347.89502 2000.85307,1175.56321 2000.87499,1000.125 Z"
                          id="seg-4"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#6600FF"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(90)}
                        />
                        <path
                          d="M1866.85924,1500.04575 L1434.05574,1250.17075 C1390.18819,1326.11535 1327.11535,1389.18819 1251.17075,1433.05574 L1501.04575,1865.85924 C1652.96234,1778.12362 1779.12362,1651.96234 1866.85924,1500.04575 Z"
                          id="seg-5"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#0000FF"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(120)}
                        />
                        <path
                          d="M1500.82924,1865.98424 L1250.95424,1433.18074 C1174.97994,1477.01483 1088.82315,1500.10307 1001.125,1500.12498 L1001.125,1999.87499 C1176.56321,1999.85307 1348.89502,1953.6794 1500.82924,1865.98424 Z"
                          id="seg-6"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#158466"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(150)}
                        />
                        <path
                          d="M1000.875,1999.87499 L1000.875,1500.12498 C913.176848,1500.10307 827.020063,1477.01483 751.045764,1433.18074 L501.170756,1865.98424 C653.104983,1953.6794 825.436793,1999.85307 1000.875,1999.87499 Z"
                          id="seg-7"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#00A933"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(180)}
                        />
                        <path
                          d="M500.95425,1865.85924 L750.829253,1433.05574 C674.88465,1389.18819 611.811812,1326.11535 567.944255,1250.17075 L135.140761,1500.04575 C222.876376,1651.96234 349.037656,1778.12362 500.95425,1865.85924 Z"
                          id="seg-8"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#81D41A"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(210)}
                        />
                        <path
                          d="M135.01576,1499.82924 L567.819265,1249.95424 C523.985169,1173.97994 500.896934,1087.82315 500.875016,1000.125 L1.12500781,1000.125 C1.14693011,1175.56321 47.3205995,1347.89502 135.01576,1499.82924 Z"
                          id="seg-9"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#FFFF00"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(240)}
                        />
                        <path
                          d="M1.12500781,999.875 L500.875016,999.875 C500.896934,912.176848 523.985169,826.020063 567.819265,750.045764 L135.01576,500.170756 C47.3205995,652.104983 1.14693011,824.436793 1.12500781,999.875 Z"
                          id="seg-10"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#FFAE42"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(270)}
                        />
                        <path
                          d="M135.140761,499.95425 L567.944255,749.829253 C611.811812,673.88465 674.88465,610.811812 750.829253,566.944255 L500.95425,134.140761 C349.037656,221.876376 222.876376,348.037656 135.140761,499.95425 Z"
                          id="seg-11"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#FF6600"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(300)}
                        />
                        <path
                          d="M501.170756,134.01576 L751.045764,566.819265 C827.020063,522.985169 913.176848,499.896934 1000.875,499.875016 L1000.875,0.125007806 C825.436793,0.146930107 653.104983,46.3205995 501.170756,134.01576 Z"
                          id="seg-12"
                          stroke="#FFFFFF"
                          strokeWidth="0.25"
                          fill="#FF4500"
                          // tslint:disable-next-line:react-this-binding-issue
                          onMouseEnter={() => this.rotateSegment(330)}
                        />
                      </g>
                      <path
                        id="line"
                        d="M994,1381.55469 L994,528.5 C994,525.186292 996.686292,522.5 1000,522.5 C1003.31371,522.5 1006,525.186292 1006,528.5 L1006,1381.55469 L1032,1381.55469 L1000,1445.55469 L968,1381.55469 L994,1381.55469 Z"
                        fill="#959595"
                        fillRule="nonzero"
                      />
                    </g>
                  </svg>

                  <FactsBox>
                    <h3>🎨 Some facts:</h3>
                    <ul>
                      <li>
                        &#9673; Complementary colors are opposite colors on the standard color
                        wheel. Using complementary colors creates contrast in an image that is
                        pleasing to the eye.
                      </li>
                      <li>
                        &#9673; The stardard, aka artist's, color wheel is based on{' '}
                        <a href="https://en.wikipedia.org/wiki/Subtractive_color">
                          subtractive color mixing
                        </a>
                        , as opposed to additive color mixing that we get from screens.
                      </li>
                      <li>
                        &#9673; With the standard color theory model, red, yellow and blue are the
                        primary colors (RYB). These are still often the primary colors of choice for
                        artists like painters and photographers, even if technology like printing
                        now uses cyan, magenta and yellow (CMY) as the primary colors for
                        subtractive color mixing.
                      </li>
                    </ul>
                  </FactsBox>
                </div>
              </PostFullContent>
            </article>
            <MoreGoodies slug="complementary-colors" />
          </main>
          <BackToTop />
          <Footer />
        </Wrapper>
      </IndexLayout>
    );
  }
}

export default ComplementaryColors;
